import { Immutable } from "immer";
import React from "react";

import type { AccordianList as Value } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";

type IProps = Immutable<{
    value: Value;
}>;

type IItem = NonNullable<Immutable<Value>["items"]>[number];

/**
 * One accordian row, owning its own disclosure state.
 *
 * Two independent toggles driven by a single click reproduce the accordian's
 * exact DOM: the panel carries no inline `display` initially (visible per its
 * cascade default, since nothing styles `.accordion__panel`) while the row
 * starts without `is-expanded`. The first click flips `is-expanded` on and
 * pins `display: block`; subsequent clicks alternate `block`/`none` in lockstep
 * with the class.
 *
 * - `href="#"` (not `javascript:void(0)`): React 19 rewrites `javascript:` URLs
 *   to a throwing placeholder, and `onToggle` calls `preventDefault` to
 *   suppress the scroll-to-top jump.
 * - `item.title` is a RawHTMLBlock and `item.body` a serialized RichText value
 *   (already `.rich-text`-wrapped), so both inject via `dangerouslySetInnerHTML`.
 */
const AccordianListItem = (
    props: Immutable<{ item: IItem }>,
): React.JSX.Element => {
    const [panelDisplay, setPanelDisplay] = React.useState<
        "" | "block" | "none"
    >("");
    const [isExpanded, setIsExpanded] = React.useState(false);
    const onToggle = (event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        setPanelDisplay((display) => (display === "block" ? "none" : "block"));
        setIsExpanded((expanded) => !expanded);
    };
    return (
        <li className={`accordion__item${isExpanded ? " is-expanded" : ""}`}>
            <EditorContent>
                {(marker) => (
                    <a
                        href="#"
                        className="accordion__trigger"
                        onClick={onToggle}
                        dangerouslySetInnerHTML={{
                            __html: props.item.title ?? "",
                        }}
                        {...marker}
                    />
                )}
            </EditorContent>
            <div
                className="accordion__panel"
                style={panelDisplay ? { display: panelDisplay } : undefined}
                dangerouslySetInnerHTML={{ __html: props.item.body ?? "" }}
            />
        </li>
    );
};

export const AccordianList = (props: IProps): React.JSX.Element => {
    return (
        <ul className="accordion accordion--border">
            {(props.value.items ?? []).map((item, index) => (
                <AccordianListItem item={item} key={index} />
            ))}
        </ul>
    );
};
